import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  ListUsersLoggedUser,
  LogedUser,
} from '../core/models/logged-user.model';
import { StorageService } from '../core/storage.service';

@Injectable({
  providedIn: 'root',
})
export class UserSessionService {
  public static readonly LOGGED_USER: string = 'loggedUser';
  public static readonly SELECTED_USER: string = 'selectedUser';
  public static readonly SELECTED_CURRENCY: string = 'selectedCurrency';
  public static readonly SESSION_MIGRATED: string = 'userSessionMigrated';
  public static readonly SESSION_LOGGED_OUT: string = 'userSessionLoggedOut';

  private readonly SESSION_KEYS: string[] = [
    UserSessionService.LOGGED_USER,
    UserSessionService.SELECTED_USER,
    UserSessionService.SELECTED_CURRENCY,
  ];

  private readonly loggedUserSubject: BehaviorSubject<LogedUser | null> =
    new BehaviorSubject<LogedUser | null>(null);
  public readonly loggedUser$: Observable<LogedUser | null> =
    this.loggedUserSubject.asObservable();

  private readonly userSelectedSubject: BehaviorSubject<ListUsersLoggedUser | null> =
    new BehaviorSubject<ListUsersLoggedUser | null>(null);
  public readonly userSelected$: Observable<ListUsersLoggedUser | null> =
    this.userSelectedSubject.asObservable();

  private readonly currencySelectedSubject: BehaviorSubject<string | null> =
    new BehaviorSubject<string | null>(null);
  public readonly currencySelected$: Observable<string | null> =
    this.currencySelectedSubject.asObservable();

  constructor(
    private readonly sessionStorageService: StorageService,
    private readonly router: Router,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {
    this.migrateFromSessionStorage();
    this.hydrateFromLocalStorage();
    this.listenToStorageEvents();
  }

  public set loggedUser(loggedUser: any) {
    this.loggedUserSubject.next(loggedUser);
    this.sessionStorageService.setObjectSessionStorage(
      UserSessionService.LOGGED_USER,
      loggedUser
    );
  }

  public set userSelected(userSelected: any) {
    this.userSelectedSubject.next(userSelected);
    this.sessionStorageService.setObjectSessionStorage(
      UserSessionService.SELECTED_USER,
      userSelected
    );
  }

  public set currencySelected(currencySelected: any) {
    if (this.currencySelectedSubject.value === currencySelected) {
      return;
    }

    this.currencySelectedSubject.next(currencySelected);
    this.sessionStorageService.setObjectSessionStorage(
      UserSessionService.SELECTED_CURRENCY,
      currencySelected
    );
  }

  clear(): void {
    this.SESSION_KEYS.forEach((key) => {
      this.sessionStorageService.removeSessionStorage(key);
      sessionStorage.removeItem(key);
    });
    localStorage.setItem(UserSessionService.SESSION_LOGGED_OUT, 'true');
    sessionStorage.removeItem(UserSessionService.SESSION_MIGRATED);
    this.loggedUserSubject.next(null);
    this.userSelectedSubject.next(null);
    this.currencySelectedSubject.next(null);
  }

  clearMigrationMark(): void {
    localStorage.removeItem(UserSessionService.SESSION_LOGGED_OUT);
    localStorage.removeItem(UserSessionService.SESSION_MIGRATED);
    sessionStorage.removeItem(UserSessionService.SESSION_MIGRATED);
  }

  private migrateFromSessionStorage(): void {
    const SKIP_RESTORE =
      localStorage.getItem(UserSessionService.SESSION_LOGGED_OUT) != null;
    let migratedLegacyData = false;
    this.SESSION_KEYS.forEach((key) => {
      const LOCAL_VALUE = localStorage.getItem(key);
      const SESSION_VALUE = sessionStorage.getItem(key);
      if (!SKIP_RESTORE && LOCAL_VALUE == null && SESSION_VALUE != null) {
        localStorage.setItem(key, SESSION_VALUE);
        migratedLegacyData = true;
      }
      if (SESSION_VALUE != null) {
        sessionStorage.removeItem(key);
      }
    });
    localStorage.removeItem(UserSessionService.SESSION_MIGRATED);
    if (migratedLegacyData) {
      sessionStorage.setItem(UserSessionService.SESSION_MIGRATED, 'true');
    }
  }

  private hydrateFromLocalStorage(): void {
    this.loggedUserSubject.next(
      this.parseStored<LogedUser>(UserSessionService.LOGGED_USER)
    );
    this.userSelectedSubject.next(
      this.parseStored<ListUsersLoggedUser>(UserSessionService.SELECTED_USER)
    );
    this.currencySelectedSubject.next(
      this.parseStored<string>(UserSessionService.SELECTED_CURRENCY)
    );
  }

  private listenToStorageEvents(): void {
    const VIEW = this.document.defaultView;
    if (!VIEW) {
      return;
    }
    VIEW.addEventListener('storage', (event: StorageEvent) => {
      this.onStorageEvent(event);
    });
  }

  private onStorageEvent(event: StorageEvent): void {
    if (event.storageArea !== this.document.defaultView?.localStorage) {
      return;
    }

    if (event.key === UserSessionService.LOGGED_USER) {
      this.onLoggedUserStorageChange(event.newValue);
      return;
    }

    if (event.key === UserSessionService.SELECTED_USER) {
      this.userSelectedSubject.next(
        this.parseJson<ListUsersLoggedUser>(event.newValue)
      );
      return;
    }

    if (event.key === UserSessionService.SELECTED_CURRENCY) {
      this.currencySelectedSubject.next(this.parseJson<string>(event.newValue));
    }
  }

  private onLoggedUserStorageChange(newValue: string | null): void {
    if (newValue == null) {
      this.loggedUserSubject.next(null);
      void this.router.navigate(['session-expired']);
      return;
    }

    const NEXT_USER = this.parseJson<LogedUser>(newValue);
    const CURRENT = this.loggedUserSubject.value;
    if (
      CURRENT &&
      NEXT_USER &&
      (CURRENT.id !== NEXT_USER.id || CURRENT.agency_id !== NEXT_USER.agency_id)
    ) {
      this.document.location.reload();
      return;
    }

    this.loggedUserSubject.next(NEXT_USER);
  }

  private parseStored<T>(key: string): T | null {
    return this.parseJson<T>(this.sessionStorageService.getSessionStorage(key));
  }

  private parseJson<T>(raw: string | null): T | null {
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }
}
